import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Upload, FolderOpen, Activity, CreditCard, Clock, AlertTriangle, Bone, HeartPulse, CircleDot, TrendingUp } from 'lucide-react';

export default function DashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/dashboard/stats').then(r => setStats(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const sub = stats?.subscription || user?.subscription || {};
  const isActive = sub.plan && sub.plan !== 'none';
  const validUntil = sub.valid_until ? new Date(sub.valid_until) : null;
  const isPlanActive = validUntil && validUntil > new Date();

  return (
    <div className="space-y-6" data-testid="dashboard-container">
      {/* Disclaimer banner */}
      <div data-testid="medical-disclaimer-banner" className="bg-amber-50 border border-amber-200 text-amber-900 px-4 py-3 rounded-lg text-sm flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 mt-0.5 shrink-0" />
        <div>
          <span className="font-semibold">Decision Support System</span> — This AI assistant provides supplementary analysis only. The practitioner must apply independent clinical judgment for all diagnoses and prescriptions.
        </div>
      </div>

      {/* Welcome */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl sm:text-4xl font-heading font-medium tracking-tight text-stone-900">
            Welcome, Dr. {user?.name?.split(' ')[0] || 'Doctor'}
          </h1>
          <p className="text-stone-500 mt-1">{user?.clinic_name || 'Your AI-powered radiology assistant'}</p>
        </div>
        <Button data-testid="quick-upload-btn" onClick={() => navigate('/new-case')} className="bg-[#1A4D2E] hover:bg-[#143B23] text-white gap-2 h-11 px-6 rounded-lg">
          <Upload className="w-4 h-4" /> New Case
        </Button>
      </div>

      {/* Case type quick actions */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="dashboard-case-type-cta">
        <button
          data-testid="cta-chest-xray"
          onClick={() => navigate('/new-case?type=chest_xray')}
          className="text-left rounded-xl border border-stone-200 bg-white hover:border-[#1A4D2E] hover:shadow-sm transition-all p-4 flex items-center gap-3"
        >
          <div className="w-11 h-11 rounded-lg bg-[#1A4D2E] text-white flex items-center justify-center">
            <HeartPulse className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <p className="font-heading font-semibold text-stone-900 text-sm">New Chest X-Ray</p>
            <p className="text-xs text-stone-500 mt-0.5">Pneumonia, TB, effusion, cardiomegaly & more</p>
          </div>
        </button>
        <button
          data-testid="cta-extremity"
          onClick={() => navigate('/new-case?type=extremity')}
          className="text-left rounded-xl border border-stone-200 bg-white hover:border-amber-500 hover:shadow-sm transition-all p-4 flex items-center gap-3"
        >
          <div className="w-11 h-11 rounded-lg bg-amber-500 text-white flex items-center justify-center">
            <Bone className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <span className="font-heading font-semibold text-stone-900 text-sm flex items-center gap-2">
              New Extremity X-Ray <Badge className="bg-amber-500 text-white text-[9px] px-1.5 py-0">NEW</Badge>
            </span>
            <p className="text-xs text-stone-500 mt-0.5">Fractures, dislocations, arthritis, foreign bodies</p>
          </div>
        </button>
        <button
          data-testid="cta-abdominal"
          onClick={() => navigate('/new-case?type=abdominal')}
          className="text-left rounded-xl border border-stone-200 bg-white hover:border-blue-500 hover:shadow-sm transition-all p-4 flex items-center gap-3"
        >
          <div className="w-11 h-11 rounded-lg bg-blue-500 text-white flex items-center justify-center">
            <CircleDot className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <span className="font-heading font-semibold text-stone-900 text-sm flex items-center gap-2">
              New Abdominal X-Ray <Badge className="bg-amber-500 text-white text-[9px] px-1.5 py-0">NEW</Badge>
            </span>
            <p className="text-xs text-stone-500 mt-0.5">Obstruction, free air, renal stones, ileus</p>
          </div>
        </button>
      </div>

      {/* Usage cap warning (only for metered plans nearing/over cap) */}
      {stats?.usage?.is_metered && stats.usage.warning && (
        <div
          data-testid="usage-warning-banner"
          className={`px-4 py-3 rounded-lg text-sm flex items-start gap-3 border ${stats.usage.overage_count > 0 ? 'bg-red-50 border-red-200 text-red-900' : 'bg-amber-50 border-amber-200 text-amber-900'}`}
        >
          <TrendingUp className="w-5 h-5 mt-0.5 shrink-0" />
          <div className="flex-1">
            <p className="font-semibold">{stats.usage.overage_count > 0 ? 'Monthly cap exceeded' : 'Approaching monthly cap'}</p>
            <p className="text-xs mt-0.5">{stats.usage.warning}</p>
          </div>
          <Button size="sm" variant="outline" onClick={() => navigate('/subscription')} className={`text-xs shrink-0 ${stats.usage.overage_count > 0 ? 'border-red-400 text-red-800 hover:bg-red-100' : 'border-amber-400 text-amber-800 hover:bg-amber-100'}`}>
            Upgrade plan
          </Button>
        </div>
      )}

      {/* Stats grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
        <Card className="border-stone-200 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-stone-500">Total Cases</p>
                <p className="text-2xl font-mono text-stone-900 mt-1">{loading ? '—' : stats?.total_cases || 0}</p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-stone-100 flex items-center justify-center">
                <FolderOpen className="w-5 h-5 text-stone-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-stone-200 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-stone-500">Analyzed</p>
                <p className="text-2xl font-mono text-stone-900 mt-1">{loading ? '—' : stats?.analyzed_cases || 0}</p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-[#1A4D2E]/10 flex items-center justify-center">
                <Activity className="w-5 h-5 text-[#1A4D2E]" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-stone-200 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-stone-500">
                  {stats?.usage?.is_metered ? 'This Cycle' : 'Credits'}
                </p>
                {stats?.usage?.is_metered ? (
                  <>
                    <p className="text-2xl font-mono text-stone-900 mt-1" data-testid="usage-used-value">
                      {stats.usage.used}<span className="text-stone-400 text-lg">/{stats.usage.cap}</span>
                    </p>
                    {stats.usage.overage_count > 0 && (
                      <p className="text-[11px] text-red-700 mt-0.5" data-testid="overage-amount">
                        +{stats.usage.overage_count} overage · ₹{stats.usage.overage_amount_inr}
                      </p>
                    )}
                  </>
                ) : (
                  <p className="text-2xl font-mono text-stone-900 mt-1">{sub.credits ?? 0}</p>
                )}
              </div>
              <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center shrink-0">
                <CreditCard className="w-5 h-5 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-stone-200 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-stone-500">Plan</p>
                <p className="text-sm font-medium text-stone-900 mt-1">
                  {isPlanActive ? sub.plan.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) : sub.plan === 'free_trial' || sub.plan === 'demo' ? 'Demo Plan' : 'No Plan'}
                </p>
                {validUntil && isPlanActive && (
                  <p className="text-xs text-stone-400 flex items-center gap-1 mt-0.5">
                    <Clock className="w-3 h-3" /> Until {validUntil.toLocaleDateString()}
                  </p>
                )}
              </div>
              {!isPlanActive && sub.plan !== 'free_trial' && sub.credits <= 0 && (
                <Button size="sm" variant="outline" onClick={() => navigate('/subscription')} className="text-xs border-[#1A4D2E] text-[#1A4D2E]">
                  Upgrade
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent cases */}
      <Card className="border-stone-200 shadow-sm">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg font-heading text-stone-900">Recent Cases</CardTitle>
            <Button variant="ghost" size="sm" onClick={() => navigate('/history')} className="text-[#1A4D2E] text-sm">
              View All
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="h-12 bg-stone-100 rounded-lg animate-pulse" />)}</div>
          ) : !stats?.recent_cases?.length ? (
            <div className="text-center py-12">
              <Upload className="w-12 h-12 text-stone-300 mx-auto mb-3" />
              <p className="text-stone-500 mb-4">No cases yet. Choose an X-ray type to get started.</p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-2">
                <Button onClick={() => navigate('/new-case?type=chest_xray')} className="bg-[#1A4D2E] hover:bg-[#143B23] text-white gap-2">
                  <HeartPulse className="w-4 h-4" /> Chest X-Ray
                </Button>
                <Button onClick={() => navigate('/new-case?type=extremity')} variant="outline" className="border-amber-400 text-amber-700 hover:bg-amber-50 gap-2">
                  <Bone className="w-4 h-4" /> Extremity X-Ray
                </Button>
                <Button onClick={() => navigate('/new-case?type=abdominal')} variant="outline" className="border-blue-400 text-blue-700 hover:bg-blue-50 gap-2">
                  <CircleDot className="w-4 h-4" /> Abdominal X-Ray
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              {stats.recent_cases.map(c => {
                const ct = c.case_type === 'bone_fracture' ? 'extremity' : (c.case_type || 'chest_xray');
                const isExt = ct === 'extremity';
                const isAbd = ct === 'abdominal';
                const IconC = isAbd ? CircleDot : isExt ? Bone : HeartPulse;
                const bg = isAbd ? 'bg-blue-100 text-blue-700' : isExt ? 'bg-amber-100 text-amber-700' : 'bg-stone-100 text-stone-600';
                const typeBadge = isAbd ? { label: 'Abdominal', cls: 'border-blue-200 text-blue-700' } : isExt ? { label: 'Extremity', cls: 'border-amber-200 text-amber-700' } : null;
                return (
                  <div
                    key={c.id}
                    data-testid={`case-row-${c.id}`}
                    onClick={() => navigate(`/case/${c.id}`)}
                    className="flex items-center justify-between p-3 rounded-lg border border-stone-100 hover:border-stone-200 hover:bg-stone-50 cursor-pointer transition-all"
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded flex items-center justify-center ${bg}`}>
                        <IconC className="w-4 h-4" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-stone-900">{c.patient_name}</p>
                        <p className="text-xs text-stone-400">
                          {c.patient_id && <span className="font-mono text-[#1A4D2E] mr-1">{c.patient_id}</span>}
                          {c.patient_age}y, {c.patient_gender} — {new Date(c.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {typeBadge && (
                        <Badge variant="outline" className={`text-[10px] ${typeBadge.cls}`}>{typeBadge.label}</Badge>
                      )}
                      <Badge variant={c.status === 'analyzed' ? 'default' : c.status === 'analyzing' ? 'secondary' : 'outline'}
                        className={c.status === 'analyzed' ? 'bg-[#1A4D2E] text-white' : ''}>
                        {c.status}
                      </Badge>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
